import { isResponseError, TResponse } from 'models/apiClient';
import { EPerPages, ISortOptions } from '../models/table';
import { queryStringWithSort } from '../helpers/request';
import { apiClient } from '../utils/apiClient';
import {
  ECaseRenewalStatus,
  IApplicationComments,
  IAssignedUsersResponse,
  ICase,
  ICaseAttachmentResponse,
  ICaseDecision,
  ICaseDocumentsRequest,
  ICaseFile,
  ICaseForLeResponse,
  ICaseHistoryResponse,
  ICaseLicencesResponse,
  ICaseManagementTaskResponse,
  ICaseOtherCommentFormValue,
  ICaseOtherCommentsResponse,
  ICaseRenewalStatus,
  ICaseRequest,
  ICaseResponse,
  ICaseSearchForLe,
  ICaseReprintLicence,
  ICaseThread,
  ICaseVariationStatus,
  ICaseWorker,
  ICommentFormValues,
  IComments,
  IEditTaskFormValue,
  IFileReview,
  INote,
  INoteFormValue,
  INotesResponse,
  IOtherCaseRequest,
  IReport,
  IReportCreateRequest,
  IReportEditRequest,
  IReportFormValue,
  IReportsResponse,
  ISummaryReportData,
  ITaskComments,
  ITaskFormValue,
  ITasksResponse,
  IThreadFormValues,
  IWeaponLicenceTemplates,
} from '../models/cases';
import { ApiEndpoints } from '../models/apiEndpoints';
import { RequestHeaderKeyTypes, RequestHeaderValueTypes } from 'constants/RequestHeaderTypes';
import { clearObject } from 'helpers/objects';

const getAssignedUsersCase = async (
  page: number,
  size: EPerPages,
  body: Partial<any> = {},
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<IAssignedUsersResponse>>(
    `${ApiEndpoints.ASSIGNED_USERS_CASE()}?${qString}`,
    body,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCases = async (
  page: number,
  size: EPerPages,
  body: Partial<any> = {},
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<ICaseResponse>>(
    `${ApiEndpoints.GET_CASES()}?${qString}`,
    body,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCase = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICase>>(ApiEndpoints.GET_CASE(id));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getRenewalStatus = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseRenewalStatus>>(
    ApiEndpoints.RENEWAL_CASE_STATUS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getVariationStatus = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseVariationStatus>>(
    ApiEndpoints.VARIATION_CASE_STATUS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getFileReview = async (fileId: string) => {
  const { data } = await apiClient.get<TResponse<IFileReview>>(
    ApiEndpoints.GET_APPLICATION_FILE_REVIEW(fileId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getApplicationComments = async (fileId: string) => {
  const { data } = await apiClient.get<TResponse<IApplicationComments[]>>(
    ApiEndpoints.GET_APPLICATION_COMMENTS(fileId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getWorkers = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseWorker[]>>(
    ApiEndpoints.GET_CASE_WORKERS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getCaseLicences = async (
  id: string,
  page: number,
  size: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.get<TResponse<ICaseLicencesResponse>>(
    `${ApiEndpoints.GET_CASE_LICENCES(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getFilesData = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseFile[]>>(
    ApiEndpoints.GET_APPLICATION_FILES(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getAttachmentsData = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseAttachmentResponse[]>>(
    ApiEndpoints.GET_CASE_ATTACHMENTS_FILES(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const downloadAllAttachmentFiles = async (id: string, typeId: string) => {
  try {
    const { data } = await apiClient.get(
      ApiEndpoints.DOWNLOAD_CASE_ATTACHMENTS_FILES(id, typeId),
      {
        responseType: 'blob',
      },
    );
    return URL.createObjectURL(data);
  } catch (e) {
    return null;
  }
};
const downloadAttachmentFile = async (id: string) => {
  try {
    const { data } = await apiClient.get(ApiEndpoints.DOWNLOAD_CASE_ATTACHMENTS_FILE(id));

    if (isResponseError(data) || !data?.url) {
      return null;
    }
    return data.url;
  } catch (e) {
    return null;
  }
};
const previewAttachmentFile = async (id: string) => {
  try {
    const { data } = await apiClient.get(ApiEndpoints.PREVIEW_CASE_ATTACHMENTS_FILE(id));

    if (isResponseError(data) || !data?.url) {
      return null;
    }
    return data.url;
  } catch (e) {
    return null;
  }
};

export const getFilePreview = async (id: string) => {
  try {
    const { data } = await apiClient.get(ApiEndpoints.GET_CASE_FILE_PREVIEW(id));
    if (isResponseError(data) || !data?.url) {
      return null;
    }
    return data.url;
  } catch (e) {
    return null;
  }
};
export const getFileDownload = async (id: string) => {
  try {
    const { data } = await apiClient.get(ApiEndpoints.GET_CASE_FILE_DOWNLOAD(id));
    if (isResponseError(data) || !data?.url) {
      return null;
    }
    return data.url;
  } catch (e) {
    return null;
  }
};

const getWeaponLicenceTemplates = async (id: string, query: string) => {
  const { data } = await apiClient.post<TResponse<IWeaponLicenceTemplates[]>>(
    ApiEndpoints.GET_WEAPON_LICENSES_CASES_TEMPLATES_SEARCH(),
    { legalUniqueId: id, query },
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createCase = async (body: ICaseRequest) => {
  const { applicationFiles, documents, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );

  if (applicationFiles) {
    for (let i = 0; i < applicationFiles.length; i++) {
      formData.append('application_files', applicationFiles[i], applicationFiles[i].name);
    }
  }
  if (documents) {
    for (let i = 0; i < documents.length; i++) {
      formData.append('documents', documents[i], documents[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_CASE(),
    formData,
    config,
  );

  return result;
};
const createOtherCase = async (body: IOtherCaseRequest) => {
  return await apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_OTHER_CASE(), body);
};

const addAttachmentsCase = async (body: ICaseDocumentsRequest) => {
  const { documents, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );
  if (documents) {
    for (let i = 0; i < documents.length; i++) {
      formData.append('documents', documents[i], documents[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.ADD_CASE_ATTACHMENTS_FILES(),
    formData,
    config,
  );

  return result;
};
const addnewTypeAttachmentsCase = async (caseId: string, body: ICaseDocumentsRequest) => {
  const { documents, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );
  if (documents) {
    for (let i = 0; i < documents.length; i++) {
      formData.append('documents', documents[i], documents[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.ADD_CASE_ATTACHMENTS_FILES_NEW_TYPE(caseId),
    formData,
    config,
  );

  return result;
};
const deleteAttachmentsCase = async (id: string) => {
  return await apiClient.delete<TResponse<any>>(
    ApiEndpoints.DELETE_CASE_ATTACHMENTS_FILES(id),
  );
};
const verificationCase = async (id: string) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.VERIFICATION_CASE(id));

  return result;
};
const finishVerificationCase = async (id: string) => {
  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.FINISH_VERIFICATION_CASE(id),
  );

  return result;
};

const assignCase = async (id: string, workerUniqueId: string) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.ASSIGN_CASE(id), {
    workerUniqueId: workerUniqueId,
  });

  return result;
};

const getLicencesForReprint = async (id: string, search?: string) => {
  const { data } = await apiClient.post<TResponse<ICaseReprintLicence[]>>(
    ApiEndpoints.SEARCH_CASE_LICENCES_FOR_REPRINT(id),
    { search: search ?? null },
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const reprintLicence = async (id: string) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.REPRINT_CASE_LICENCE(id));

  return result;
};

const closeCase = async (id: string) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.CLOSE_CASE(id));

  return result;
};

const closeTask = async (id: string, summary: string) => {
  const result = await apiClient.patch<TResponse<any>>(ApiEndpoints.CLOSE_TASK(id), {
    summary: summary,
  });
  return result;
};

const approveCase = async (
  id: string,
  body: { reasonUniqueIds: string[]; comment: string },
) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.APPROVE_CASE(id), body);

  return result;
};

const refuseCase = async (
  id: string,
  body: { reasonUniqueIds: string[]; comment: string },
) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.REFUSE_CASE(id), body);

  return result;
};

const rejectCase = async (
  id: string,
  body: { reasonUniqueIds: string[]; comment: string },
) => {
  const result = await apiClient.post<TResponse<any>>(ApiEndpoints.REJECT_CASE(id), body);

  return result;
};

const getCaseDecision = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseDecision>>(
    ApiEndpoints.GET_CASE_DECISION(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const updatePayment = async (id: string, paymentStatusUniqueId: string) => {
  const result = await apiClient.patch<TResponse<any>>(ApiEndpoints.CASE_UPDATE_PAYMENT(id), {
    paymentStatusUniqueId: paymentStatusUniqueId,
  });

  return result;
};

const getLegalEntityCases = async (
  id: string,
  page: number,
  size: EPerPages,
  body: ICaseSearchForLe,
  sort?: ISortOptions | null,
) => {
  const qString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.post<ICaseForLeResponse>(
    `${ApiEndpoints.GET_CASES_FOR_LE(id)}?${qString}`,
    clearObject(body),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createApplicationComment = async (id: string, body: ICommentFormValues) => {
  const result = await apiClient.post<TResponse<IApplicationComments>>(
    ApiEndpoints.ADD_APPLICATION_COMMENT(id),
    body,
  );
  return result;
};
const updateApplicationComment = async (
  id: string,
  threadId: string,
  body: IThreadFormValues,
) => {
  const result = await apiClient.patch<TResponse<IApplicationComments>>(
    ApiEndpoints.UPDATE_APPLICATION_COMMENT_THREAD(id, threadId),
    body,
  );
  return result;
};

const createApplicationCommentForThread = async (
  id: string,
  threadId: string,
  comment: string,
) => {
  const result = await apiClient.post<TResponse<IComments>>(
    ApiEndpoints.ADD_APPLICATION_COMMENT_FOR_THREAD(id, threadId),
    { comment },
  );
  return result;
};
const updateApplicationCommentForThread = async (
  id: string,
  commentId: string,
  comment: string,
) => {
  const result = await apiClient.patch<TResponse<IComments>>(
    ApiEndpoints.UPDATE_APPLICATION_COMMENT(id, commentId),
    { comment },
  );
  return result;
};

const getCaseThreads = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ICaseThread[]>>(
    ApiEndpoints.GET_CASE_THREADS(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createCaseThread = async (id: string, body: ICommentFormValues) => {
  const result = await apiClient.post<TResponse<ICaseThread>>(
    ApiEndpoints.ADD_CASE_THREAD(id),
    body,
  );
  return result;
};

const createCaseThreadComment = async (id: string, threadId: string, comment: string) => {
  const result = await apiClient.post<TResponse<IComments>>(
    ApiEndpoints.ADD_CASE_THREAD_COMMENT(id, threadId),
    { comment },
  );
  return result;
};

const getCaseNotes = async (
  id: string,
  page: number,
  size: EPerPages,
  sort?: ISortOptions | null,
) => {
  const qString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.get<INotesResponse>(
    `${ApiEndpoints.GET_CASE_NOTES(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getCaseHistory = async (
  id: string,
  page: number,
  size: EPerPages,
  sort?: ISortOptions | null,
) => {
  const qString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.get<ICaseHistoryResponse>(
    `${ApiEndpoints.GET_CASE_HISTORY(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getCaseTasks = async (
  id: string,
  page: number,
  size: EPerPages,
  sort?: ISortOptions | null,
) => {
  const qString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.get<ITasksResponse>(
    `${ApiEndpoints.GET_CASE_TASKS(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createCaseTask = async (id: string, body: ITaskFormValue) => {
  return await apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_CASE_TASK(id), body);
};
const createCaseTaskComment = async (id: string, body: IEditTaskFormValue) => {
  return await apiClient.patch<TResponse<any>>(
    ApiEndpoints.CREATE_CASE_TASK_COMMENT(id),
    body,
  );
};

const getCaseTask = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ITaskComments>>(
    ApiEndpoints.GET_CASE_TASK(id),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getCaseNote = async (noteId: string) => {
  const { data } = await apiClient.get<TResponse<INote>>(ApiEndpoints.GET_CASE_NOTE(noteId));

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const createCaseNote = async (id: string, body: INoteFormValue) => {
  return await apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_CASE_NOTE(id), body);
};
const updateCaseNote = async (id: string, noteId: string, body: INoteFormValue) => {
  return await apiClient.patch<TResponse<any>>(
    ApiEndpoints.UPDATE_CASE_NOTE(id, noteId),
    body,
  );
};
const deleteCaseNote = async (id: string, noteId: string) => {
  return await apiClient.delete<TResponse<any>>(ApiEndpoints.DELETE_CASE_NOTE(id, noteId));
};
const deleteCaseTask = async (id: string) => {
  return await apiClient.delete<TResponse<any>>(ApiEndpoints.DELETE_CASE_TASK(id));
};

const getCaseReports = async (
  id: string,
  page: number,
  size: EPerPages,
  sort?: ISortOptions | null,
) => {
  const qString = queryStringWithSort({ page: page - 1, size }, sort);
  const { data } = await apiClient.get<IReportsResponse>(
    `${ApiEndpoints.GET_CASE_REPORTS(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCaseReport = async (reportId: string) => {
  const { data } = await apiClient.get<TResponse<IReport>>(
    ApiEndpoints.GET_CASE_REPORT(reportId),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createCaseReport = async (id: string, body: IReportFormValue) => {
  return await apiClient.post<TResponse<any>>(ApiEndpoints.CREATE_CASE_REPORT(id), body);
};

const createCaseReportResult = async (id: string, body: IReportCreateRequest) => {
  const { attachments, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );
  if (attachments) {
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i], attachments[i].name);
    }
  }

  const result = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_CASE_REPORT_RESULT(id),
    formData,
    config,
  );

  return result;
};

const updateCaseReport = async (reportId: string, body: IReportEditRequest) => {
  const { attachments, request } = body;

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };
  const formData = new FormData();

  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: 'application/json',
    }),
  );
  if (attachments) {
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i], attachments[i].name);
    }
  }

  const result = await apiClient.patch<TResponse<any>>(
    ApiEndpoints.UPDATE_CASE_REPORT(reportId),
    formData,
    config,
  );

  return result;
};
const deleteCaseReport = async (reportId: string) => {
  return await apiClient.delete<TResponse<any>>(ApiEndpoints.DELETE_CASE_REPORT(reportId));
};
const generateSummaryReport = async (reportId: string) => {
  return await apiClient.post<TResponse<{ url: string }>>(
    ApiEndpoints.GENERATE_CASE_SUMMARY_REPORT(reportId),
  );
};
const getSummaryReportData = async (reportId: string) => {
  const { data } = await apiClient.get<TResponse<ISummaryReportData>>(
    ApiEndpoints.GET_CASE_SUMMARY_REPORT(reportId),
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const downloadSummaryReportData = async (reportId: string) => {
  const { data } = await apiClient.get<TResponse<ISummaryReportData>>(
    ApiEndpoints.DOWNLOAD_CASE_SUMMARY_REPORT(reportId),
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getProcessTypeOtherWorkers = async (licenceAuthorityUniqueId: string) => {
  const { data } = await apiClient.post<TResponse<ICaseWorker[]>>(
    ApiEndpoints.GET_CASE_OTHER_WORKERS(),
    { licenceAuthorityUniqueId: licenceAuthorityUniqueId },
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCaseOtherComments = async (
  id: string,
  page: number,
  size: EPerPages,
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.get<TResponse<ICaseOtherCommentsResponse>>(
    `${ApiEndpoints.GET_CASE_OTHER_COMMENTS(id)}?${qString}`,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createCaseOtherComment = async (id: string, body: ICaseOtherCommentFormValue) => {
  return await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_CASE_OTHER_COMMENT(id),
    body,
  );
};

const getCaseDashboard = async (period: string, licenceAuthorityUniqueIds: string[]) => {
  try {
    const { data } = await apiClient.get(
      `${ApiEndpoints.GET_CASE_DASHBOARD()}?period=${period}&licenceAuthorityUniqueIds=${licenceAuthorityUniqueIds.join(',')}`,
    );
    if (isResponseError(data)) {
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

const getCaseManagementTask = async (
  page: number,
  size: EPerPages,
  body: Partial<any> = {},
  sort: ISortOptions | null | undefined,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<ICaseManagementTaskResponse>>(
    `${ApiEndpoints.GET_CASE_MANAGEMENT()}?${qString}`,
    body,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

export {
  getCases,
  getCase,
  createCase,
  getWeaponLicenceTemplates,
  getLegalEntityCases,
  verificationCase,
  finishVerificationCase,
  getFilesData,
  updatePayment,
  getAttachmentsData,
  downloadAttachmentFile,
  previewAttachmentFile,
  addAttachmentsCase,
  addnewTypeAttachmentsCase,
  assignCase,
  getLicencesForReprint,
  reprintLicence,
  closeCase,
  approveCase,
  refuseCase,
  rejectCase,
  getCaseDecision,
  getWorkers,
  getFileReview,
  getApplicationComments,
  createApplicationComment,
  createApplicationCommentForThread,
  updateApplicationCommentForThread,
  updateApplicationComment,
  getCaseThreads,
  createCaseThread,
  createCaseThreadComment,
  deleteAttachmentsCase,
  getCaseNotes,
  createCaseNote,
  updateCaseNote,
  deleteCaseNote,
  getCaseNote,
  getCaseHistory,
  getCaseTasks,
  createCaseTask,
  createCaseTaskComment,
  getCaseTask,
  deleteCaseTask,
  downloadAllAttachmentFiles,
  getAssignedUsersCase,
  getCaseReports,
  getCaseReport,
  createCaseReportResult,
  createCaseReport,
  updateCaseReport,
  deleteCaseReport,
  getCaseLicences,
  generateSummaryReport,
  getSummaryReportData,
  getProcessTypeOtherWorkers,
  createOtherCase,
  getCaseOtherComments,
  createCaseOtherComment,
  closeTask,
  downloadSummaryReportData,
  getCaseDashboard,
  getCaseManagementTask,
  getRenewalStatus,
  getVariationStatus,
};
